import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
    FileText,
    Download,
    Clock,
    CheckCircle,
    AlertCircle
} from 'lucide-react';

interface Invoice {
    id: string;
    invoice_number: string;
    total_amount: number;
    currency: string;
    status: 'draft' | 'approved' | 'paid';
    pdf_url?: string;
    created_at: any;
    period_start: any;
    period_end: any;
}

export default function AffiliateBilling() {
    const { user } = useAuth();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalPending, setTotalPending] = useState(0);
    const [totalPaid, setTotalPaid] = useState(0);

    useEffect(() => {
        if (user?.uid) {
            fetchInvoices();
        }
    }, [user?.uid]);

    const fetchInvoices = async () => {
        if (!user?.uid) return;

        try {
            const invoicesQuery = query(
                collection(db, 'invoices'),
                where('user_id', '==', user.uid),
                orderBy('created_at', 'desc')
            );
            const snapshot = await getDocs(invoicesQuery);
            const invoicesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Invoice[];

            setInvoices(invoicesList);

            // Calculate totals
            const pending = invoicesList
                .filter(i => i.status === 'approved')
                .reduce((sum, i) => sum + i.total_amount, 0);
            const paid = invoicesList
                .filter(i => i.status === 'paid')
                .reduce((sum, i) => sum + i.total_amount, 0);

            setTotalPending(pending);
            setTotalPaid(paid);
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'draft':
                return <span className="badge badge-warning">Draft</span>;
            case 'approved':
                return <span className="badge badge-info">Approved</span>;
            case 'paid':
                return <span className="badge badge-success">Paid</span>;
            default:
                return <span className="badge">{status}</span>;
        }
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '-';
        return timestamp.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-white">Billing</h1>
                <p className="text-gray-400">View your invoices and payment history</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="stat-card">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                            <Clock className="text-amber-400" size={24} />
                        </div>
                        <div>
                            <p className="text-gray-400 text-sm">Pending Payment</p>
                            <p className="text-2xl font-bold text-white">€{totalPending.toFixed(2)}</p>
                        </div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                            <CheckCircle className="text-emerald-400" size={24} />
                        </div>
                        <div>
                            <p className="text-gray-400 text-sm">Total Paid</p>
                            <p className="text-2xl font-bold text-white">€{totalPaid.toFixed(2)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Invoices Table */}
            <div className="card">
                <h2 className="text-lg font-semibold text-white mb-4">Invoice History</h2>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : invoices.length === 0 ? (
                    <div className="text-center py-12">
                        <FileText className="mx-auto text-gray-500 mb-4" size={48} />
                        <h3 className="text-lg font-semibold text-white mb-2">No Invoices Yet</h3>
                        <p className="text-gray-400">Your invoices will appear here once generated.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-gray-400 text-sm border-b border-white/10">
                                    <th className="pb-4 font-medium">Invoice</th>
                                    <th className="pb-4 font-medium">Period</th>
                                    <th className="pb-4 font-medium">Amount</th>
                                    <th className="pb-4 font-medium">Status</th>
                                    <th className="pb-4 font-medium">Date</th>
                                    <th className="pb-4 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((invoice) => (
                                    <tr key={invoice.id} className="table-row">
                                        <td className="py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                                                    <FileText className="text-primary-400" size={18} />
                                                </div>
                                                <span className="font-mono text-white">{invoice.invoice_number}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 text-gray-400">
                                            {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                                        </td>
                                        <td className="py-4">
                                            <span className="text-white font-semibold">
                                                €{invoice.total_amount.toFixed(2)}
                                            </span>
                                        </td>
                                        <td className="py-4">
                                            {getStatusBadge(invoice.status)}
                                        </td>
                                        <td className="py-4 text-gray-400">
                                            {formatDate(invoice.created_at)}
                                        </td>
                                        <td className="py-4">
                                            {invoice.pdf_url && (
                                                <a
                                                    href={invoice.pdf_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 text-primary-400 hover:text-primary-300"
                                                >
                                                    <Download size={18} />
                                                    <span className="text-sm">PDF</span>
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Payment Info */}
            <div className="card">
                <h2 className="text-lg font-semibold text-white mb-4">Payment Information</h2>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="text-blue-400 shrink-0 mt-0.5" size={20} />
                        <div>
                            <p className="text-blue-400 text-sm">
                                Payments are processed via bank transfer within 5-7 business days after invoice approval.
                                Make sure your payment details are up to date in your profile settings.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
